import { Topics } from '@app/context/constants';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { AuditReportData } from '@app/types/auditReport';
import { supervisoryAuthorities } from '@app/types/supervisoryAuthorities';
import { AlertDialog } from '@components/ui/AlertDialog/AlertDialog';
import { useDeleteDpoAuditReportHistoryMutation } from '@dieterApi/dpo/useDeleteDpoAuditReportHistoryMutation';
import { useDpoAuditReportHistoryEntryQuery } from '@dieterApi/dpo/useDpoAuditReportHistoryEntryQuery';
import { useDpoAuditReportHistoryQuery } from '@dieterApi/dpo/useDpoAuditReportHistoryQuery';
import { useDpoInfoQuery } from '@dieterApi/dpo/useDpoInfoQuery';
import { DpoState, useDpoTasksQuery } from '@dieterApi/dpo/useDpoTasksQuery';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { AuditReportForm } from './AuditReportForm';
import { AuditReportHistoryPreview } from './AuditReportHistoryPreview';
import { AuditReportHistorySidebar } from './AuditReportHistorySidebar';
import { AuditReportPreview } from './AuditReportPreview';

interface AuditReportEditorProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (data: AuditReportData, htmlContent: string) => void;
}

type EditorMode = 'history' | 'edit';

// Mapping from DPO Topics to audit report status check keys
const TOPIC_TO_STATUS_CHECK: Partial<Record<Topics, keyof AuditReportData['statusChecks']>> = {
  [Topics.VVZ]: 'verarbeitungsverzeichnis',
  [Topics.TOMs]: 'tom',
  [Topics.AV]: 'auftragsverarbeitung',
  [Topics.RIGHTS]: 'betroffenenrechte',
  [Topics.EMPLOYESS]: 'schulungen',
  [Topics.DSMGMT]: 'datenschutzorganisation',
};

// Mapping from DPO task state to audit status
const DPO_STATE_TO_AUDIT_STATUS: Record<DpoState, 'present' | 'partial' | 'missing'> = {
  [DpoState.UNTOUCHED]: 'missing',
  [DpoState.TOUCHED]: 'partial',
  [DpoState.COMPLETE]: 'partial',
  [DpoState.AUDITED]: 'present',
};

// Ranking for DPO states (higher = more advanced)
const DPO_STATE_RANK: Record<DpoState, number> = {
  [DpoState.UNTOUCHED]: 0,
  [DpoState.TOUCHED]: 1,
  [DpoState.COMPLETE]: 2,
  [DpoState.AUDITED]: 3,
};

export function AuditReportEditor({ open, onClose, onGenerate }: AuditReportEditorProps) {
  const { t } = useTranslation();
  const { user, activeCompany, isDpoMode } = useUserWithCompany();
  const { data: dpoData } = useDpoInfoQuery(activeCompany?.id);
  const dpoInfo = dpoData?.getDpoInfo;
  const { getValue } = useUserValuesData();

  // Fetch DPO tasks to auto-populate status checks
  const { data: dpoTasksData } = useDpoTasksQuery(isDpoMode ? activeCompany?.id : undefined);
  const dpoTasks = dpoTasksData?.getDpoTasks || [];

  // Fetch audit report history
  const { data: historyData, loading: historyLoading } = useDpoAuditReportHistoryQuery(activeCompany?.id);
  const history = historyData?.getDpoAuditReportHistory || [];

  // State for mode and selection
  const [mode, setMode] = useState<EditorMode>('history');
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);

  // Fetch single history entry
  const [fetchHistoryEntry, { data: historyEntryData, loading: entryLoading }] = useDpoAuditReportHistoryEntryQuery();
  const historyEntry = historyEntryData?.getDpoAuditReportHistoryEntry;

  // Delete history entry
  const [deleteHistoryEntry] = useDeleteDpoAuditReportHistoryMutation(activeCompany?.id || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);

  // Get user values for additional prefilling
  const legalForm = getValue('LEGAL_FORM') || '';
  const industry = getValue('INDUSTRY') || '';
  const companySize = getValue('COMPANY_SIZE') || '';
  const userState = getValue('STATE') || '';

  // Compute status checks from DPO task states
  const computedStatusChecks = useMemo(() => {
    const statusChecks: AuditReportData['statusChecks'] = {
      datenschutzorganisation: 'partial',
      verarbeitungsverzeichnis: 'partial',
      rechtsgrundlagen: 'partial',
      tom: 'partial',
      auftragsverarbeitung: 'partial',
      betroffenenrechte: 'partial',
      schulungen: 'partial',
    };

    // If no DPO tasks or not in DPO mode, return defaults
    if (!isDpoMode || dpoTasks.length === 0) {
      return statusChecks;
    }

    // Map each topic to its most advanced state
    const topicStates = new Map<Topics, DpoState>();
    dpoTasks.forEach((task) => {
      const topicId = task.topicId as Topics;
      const currentState = topicStates.get(topicId);

      // Keep the least advanced state for each topic (using numeric ranking)
      if (!currentState || DPO_STATE_RANK[task.dpoState] < DPO_STATE_RANK[currentState]) {
        topicStates.set(topicId, task.dpoState);
      }
    });

    // Apply the topic states to status checks
    topicStates.forEach((dpoState, topicId) => {
      const statusCheckKey = TOPIC_TO_STATUS_CHECK[topicId];
      if (statusCheckKey) {
        statusChecks[statusCheckKey] = DPO_STATE_TO_AUDIT_STATUS[dpoState];
      }
    });

    // Special case: rechtsgrundlagen also derives from DSMGMT
    const dsmgmtState = topicStates.get(Topics.DSMGMT);
    if (dsmgmtState) {
      statusChecks.rechtsgrundlagen = DPO_STATE_TO_AUDIT_STATUS[dsmgmtState];
    }

    return statusChecks;
  }, [isDpoMode, dpoTasks]);

  // Determine DSB type based on company.isDpo flag
  const dsbType: 'intern' | 'extern' = activeCompany?.isDpo ? 'intern' : 'extern';

  // Responsible person: first registered non-DPO user of the company
  const firstNonDpoUser = useMemo(() => {
    if (!activeCompany?.users || activeCompany.users.length === 0) return null;

    // Filter out DPO user and sort by createdAt
    const nonDpoUsers = activeCompany.users
      .filter((u) => u.id !== activeCompany.assignedDpoUserId)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    return nonDpoUsers.length > 0 ? nonDpoUsers[0] : null;
  }, [activeCompany]);

  const responsiblePerson =
    firstNonDpoUser?.firstName && firstNonDpoUser?.lastName
      ? `${firstNonDpoUser.firstName} ${firstNonDpoUser.lastName}`
      : '';
  const responsibleContact = firstNonDpoUser?.email || activeCompany?.phone || '';

  // Get supervisory authority info based on user's state
  const supervisoryAuthorityInfo =
    userState && supervisoryAuthorities[userState] ? supervisoryAuthorities[userState] : null;

  // Compute initial data with all available information
  const initialData = useMemo<AuditReportData>(
    () => ({
      companyName: activeCompany?.name || '',
      companyAddress: [activeCompany?.street, [activeCompany?.zip, activeCompany?.city].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', '),
      organizationalUnits: '',
      auditPeriod: '',
      reportDate: new Date().toLocaleDateString('de-DE'),
      dsbName: dpoInfo?.name || '',
      dsbCompany: dpoInfo?.company || '',
      dsbType,
      dsbContact: dpoInfo?.email || dpoInfo?.phone || '',
      dsbAppointmentDate: '',
      legalForm,
      industry,
      employeeCount: companySize,
      responsiblePerson,
      responsibleContact,
      supervisoryAuthority: supervisoryAuthorityInfo?.name || '',
      supervisoryAuthorityAddress: supervisoryAuthorityInfo?.address || '',
      supervisoryAuthorityState: supervisoryAuthorityInfo?.state || '',
      statusChecks: computedStatusChecks,
      statusNotes: {},
      sections: {
        dsms: '',
        website: '',
        auftragsverarbeitung: '',
        tom: '',
        informationspflichten: '',
        videoueberwachung: '',
        mitarbeiter: '',
        verzeichnis: '',
        betroffenenrechte: '',
        datenschutzverletzungen: '',
        dsfa: '',
        dsbPosition: '',
      },
      fazit: '',
    }),
    [
      activeCompany,
      dpoInfo,
      legalForm,
      industry,
      companySize,
      dsbType,
      responsiblePerson,
      responsibleContact,
      supervisoryAuthorityInfo,
      computedStatusChecks,
      firstNonDpoUser,
    ]
  );

  const [reportData, setReportData] = useState<AuditReportData>(initialData);

  // Ref for the preview component to print
  const previewRef = useRef<HTMLDivElement>(null);

  // React-to-print handler
  const handlePrint = useReactToPrint({
    contentRef: previewRef,
    documentTitle: `Bericht_${new Date().toLocaleDateString('de-DE')}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 20mm;
      }
    `,
  });

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setMode('history');
      setSelectedHistoryId(null);
      setReportData(initialData);
    }
  }, [open, initialData]);

  // Fetch history entry when selected
  useEffect(() => {
    if (selectedHistoryId) {
      fetchHistoryEntry({ variables: { id: selectedHistoryId } });
    }
  }, [selectedHistoryId, fetchHistoryEntry]);

  const [activeTab, setActiveTab] = useState<'form' | 'preview'>('form');

  const handleFieldChange = (field: string, value: any) => {
    setReportData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedFieldChange = (section: string, field: string, value: any) => {
    setReportData((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section as keyof AuditReportData] as any),
        [field]: value,
      },
    }));
  };

  const handleNewReport = () => {
    setMode('edit');
    setSelectedHistoryId(null);
    setReportData(initialData);
  };

  const handleSelectHistory = (id: string) => {
    setSelectedHistoryId(id);
  };

  const handleDeleteHistory = (id: string) => {
    setEntryToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (entryToDelete) {
      await deleteHistoryEntry({ variables: { id: entryToDelete } });
      if (selectedHistoryId === entryToDelete) {
        setSelectedHistoryId(null);
      }
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  const renderContent = () => {
    if (mode === 'history') {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-3 h-full">
          <div className="lg:col-span-1">
            <AuditReportHistorySidebar
              history={history}
              selectedId={selectedHistoryId}
              onSelect={handleSelectHistory}
              onNewReport={handleNewReport}
              onDelete={handleDeleteHistory}
            />
          </div>
          <div className="lg:col-span-2">
            <AuditReportHistoryPreview htmlContent={historyEntry?.htmlContent || null} loading={entryLoading} />
          </div>
        </div>
      );
    }

    // Edit mode
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
        <div className={`overflow-y-auto p-6 border-r ${activeTab === 'preview' ? 'hidden lg:block' : ''}`}>
          <AuditReportForm
            data={reportData}
            onFieldChange={handleFieldChange}
            onNestedFieldChange={handleNestedFieldChange}
          />
        </div>
        <div className={`overflow-y-auto p-6 bg-gray-50 ${activeTab === 'form' ? 'hidden lg:block' : ''}`}>
          <AuditReportPreview ref={previewRef} data={reportData} />
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ style: { height: '90vh' } }}>
        <DialogTitle className="border-b">
          <div>{mode === 'history' ? t('route.dpo.auditReport.history') : t('route.dpo.auditReport.create')}</div>
          {mode === 'edit' && (
            <div className="flex gap-2 mt-2 lg:hidden">
              <button
                onClick={() => setActiveTab('form')}
                className={`px-4 py-2 rounded ${activeTab === 'form' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                Bearbeiten
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-4 py-2 rounded ${activeTab === 'preview' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
              >
                Vorschau
              </button>
            </div>
          )}
        </DialogTitle>

        <DialogContent className="p-0">{renderContent()}</DialogContent>

        {mode === 'edit' && (
          <div className="border-t p-4 flex justify-end gap-2">
            <button onClick={() => setMode('history')} className="px-4 py-2 border rounded hover:bg-gray-50">
              Zurück
            </button>
            <button
              onClick={() => {
                // Get HTML content from preview for saving to DB
                const previewElement = previewRef.current;
                let htmlContent = previewElement?.outerHTML || '';
                // Remove sourcemap comments to prevent browser warnings when rendering
                htmlContent = htmlContent.replace(/\/\*#\s*sourceMappingURL=.*?\*\//g, '');
                htmlContent = htmlContent.replace(/\/\/#\s*sourceMappingURL=.*/g, '');
                // Save to database and trigger print dialog
                onGenerate(reportData, htmlContent);
                // Open print dialog
                handlePrint();
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {t('route.dpo.auditReport.finalize')}
            </button>
          </div>
        )}
      </Dialog>

      <AlertDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        message={t('route.dpo.auditReport.confirmDelete')}
        onAccept={confirmDelete}
      />
    </>
  );
}
